import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import WinSummary from "@/components/WinSummary";
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  User,
  Building2,
  DollarSign,
  Calendar,
  GripVertical,
  ChevronRight,
  ChevronDown,
  Phone,
  Mail,
  Globe,
  Linkedin,
  FileText,
  MessageSquare,
  CheckCircle2,
  Clock,
  X,
  Edit2,
  Trash2,
  Users,
  TrendingUp,
  Target,
  Download,
  FileSignature,
  Check,
  RefreshCw,
  CheckSquare,
  Square,
  AlertCircle,
  Inbox,
  Send as SendIcon,
  ExternalLink,
  ListTodo,
  Settings2,
  Hash,
  Upload
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import CSVImportModal from "./CSVImportModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

// Stage colors
const STAGE_COLORS = {
  lead: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  discovery: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  proposal: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  negotiation: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  won: "bg-green-500/20 text-green-300 border-green-500/30",
  lost: "bg-red-500/20 text-red-300 border-red-500/30",
};

const STAGE_BG = {
  lead: "bg-slate-900/50 border-slate-700",
  discovery: "bg-blue-900/20 border-blue-700/50",
  proposal: "bg-purple-900/20 border-purple-700/50",
  negotiation: "bg-amber-900/20 border-amber-700/50",
  won: "bg-green-900/20 border-green-700/50",
  lost: "bg-red-900/20 border-red-700/50",
};

export default function CRMPage() {
  const [view, setView] = useState("pipeline"); // pipeline | leads | contacts
  const [deals, setDeals] = useState([]);
  const [dealsByStage, setDealsByStage] = useState({});
  const [stages, setStages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pipeline state
  const [pipelines, setPipelines] = useState([]);
  const [currentPipeline, setCurrentPipeline] = useState(null);
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  
  // Modals
  const [showDealModal, setShowDealModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  
  // Drag state
  const [draggedDeal, setDraggedDeal] = useState(null);
  
  // Win celebration state
  const [showWinSummary, setShowWinSummary] = useState(false);
  const [winData, setWinData] = useState(null);

  useEffect(() => {
    fetchPipelines();
  }, []);
  
  useEffect(() => {
    if (currentPipeline) {
      fetchData(currentPipeline.id);
    }
  }, [currentPipeline]);

  const fetchPipelines = async () => {
    try {
      const res = await api.get("/crm/pipelines");
      const pipelineList = res.data.pipelines || [];
      setPipelines(pipelineList);
      
      // Set current pipeline to default or first
      const defaultPipeline = pipelineList.find(p => p.is_default) || pipelineList[0];
      if (defaultPipeline) {
        setCurrentPipeline(defaultPipeline);
      }
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("CRM requires Growth plan");
      } else {
        toast.error("Failed to load pipelines");
      }
      setLoading(false);
    }
  };

  const fetchData = async (pipelineId) => {
    setLoading(true);
    try {
      // Fetch CRM data, clients, leads, and stats in parallel
      const [dealsRes, contactsRes, leadsRes, statsRes] = await Promise.all([
        api.get(`/crm/deals${pipelineId ? `?pipeline_id=${pipelineId}` : ''}`),
        api.get("/crm/contacts?filter_type=clients"),
        api.get("/crm/leads"),
        api.get("/crm/stats")
      ]);
      
      setDeals(dealsRes.data.deals || []);
      setDealsByStage(dealsRes.data.by_stage || {});
      setStages(dealsRes.data.stages || []);
      setContacts(contactsRes.data.contacts || []);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
      
      // Note: Don't update currentPipeline here to avoid infinite loop
      // The pipeline info is already set from fetchPipelines
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("CRM requires Growth plan");
      } else {
        toast.error("Failed to load CRM data");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleConvertLead = async (leadId) => {
    try {
      await api.post(`/crm/leads/${leadId}/convert`);
      toast.success("Lead converted to client!");
      fetchData(currentPipeline?.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to convert lead");
    }
  };
  
  const handlePipelineChange = (pipelineId) => {
    const pipeline = pipelines.find(p => p.id === pipelineId);
    if (pipeline) {
      setCurrentPipeline(pipeline);
    }
  };
  
  const handleCreatePipeline = async (data) => {
    try {
      const res = await api.post("/crm/pipelines", data);
      toast.success("Pipeline created");
      await fetchPipelines();
      setCurrentPipeline(res.data);
      setShowPipelineModal(false);
      setEditingPipeline(null);
    } catch (e) {
      if (e.response?.data?.detail?.includes("Maximum")) {
        toast.error("Maximum 5 pipelines allowed");
      } else {
        toast.error("Failed to create pipeline");
      }
    }
  };
  
  const handleUpdatePipeline = async (data) => {
    try {
      await api.put(`/crm/pipelines/${editingPipeline.id}`, data);
      toast.success("Pipeline updated");
      await fetchPipelines();
      setShowPipelineModal(false);
      setEditingPipeline(null);
    } catch (e) {
      toast.error("Failed to update pipeline");
    }
  };
  
  const handleDeletePipeline = async (pipelineId) => {
    if (!window.confirm("Delete this pipeline? Deals will be moved to the default pipeline.")) {
      return;
    }
    try {
      await api.delete(`/crm/pipelines/${pipelineId}`);
      toast.success("Pipeline deleted");
      await fetchPipelines();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete pipeline");
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedDeal || draggedDeal.stage === targetStage) {
      setDraggedDeal(null);
      return;
    }

    // Optimistic update
    const updatedDeals = deals.map(d => 
      d.id === draggedDeal.id ? { ...d, stage: targetStage } : d
    );
    setDeals(updatedDeals);
    
    // Rebuild by_stage
    const newByStage = {};
    stages.forEach(s => { newByStage[s.id] = []; });
    updatedDeals.forEach(d => {
      if (newByStage[d.stage]) newByStage[d.stage].push(d);
    });
    setDealsByStage(newByStage);
    
    try {
      await api.put(`/crm/deals/${draggedDeal.id}`, { stage: targetStage });
      toast.success(`Moved to ${targetStage}`);
      
      // Trigger WinSummary celebration if deal is moved to "won"
      if (targetStage === 'won') {
        setWinData({
          clientName: draggedDeal.contact?.name || draggedDeal.title || 'Client',
          dealValue: draggedDeal.value || 0,
          stats: stats ? {
            thisMonth: stats.won_value || 0,
            dealsWon: stats.won_count || 0,
            winRate: stats.total_deals > 0 ? Math.round((stats.won_count / stats.total_deals) * 100) : 0
          } : null
        });
        setShowWinSummary(true);
      }
    } catch (e) {
      toast.error("Failed to update deal");
      fetchData(currentPipeline?.id); // Revert on error
    }
    
    setDraggedDeal(null);
  };

  // Filter deals/contacts by search
  const filteredDeals = deals.filter(d => 
    d.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.contact?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredContacts = contacts.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Win Summary Modal */}
      {showWinSummary && winData && (
        <WinSummary 
          type="deal_won" 
          data={winData} 
          onClose={() => {
            setShowWinSummary(false);
            setWinData(null);
            fetchData(currentPipeline?.id); // Refresh stats after win
          }} 
        />
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">CRM</h1>
          <p className="text-slate-400 text-sm">Manage your contacts and deals pipeline</p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Pipeline Selector - Only show in pipeline view */}
          {view === "pipeline" && pipelines.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-slate-700 bg-slate-800 text-white hover:bg-slate-700 min-w-[180px] justify-between"
                  data-testid="pipeline-selector"
                >
                  <span className="truncate">{currentPipeline?.name || "Select Pipeline"}</span>
                  <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 w-56">
                {pipelines.map((pipeline) => (
                  <DropdownMenuItem
                    key={pipeline.id}
                    onClick={() => handlePipelineChange(pipeline.id)}
                    className={currentPipeline?.id === pipeline.id ? "bg-purple-500/20 text-purple-300" : ""}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="truncate">{pipeline.name}</span>
                      <div className="flex items-center gap-2">
                        {pipeline.is_default && (
                          <Badge variant="secondary" className="text-[10px]">Default</Badge>
                        )}
                        <Badge variant="outline" className="text-[10px]">{pipeline.deal_count || 0}</Badge>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem 
                  onClick={() => {
                    setEditingPipeline(null);
                    setShowPipelineModal(true);
                  }}
                  className="text-purple-400"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Pipeline
                </DropdownMenuItem>
                {currentPipeline && !currentPipeline.is_default && (
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingPipeline(currentPipeline);
                      setShowPipelineModal(true);
                    }}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Pipeline
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* View Toggle */}
          <div className="flex bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setView("pipeline")}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                view === "pipeline" ? "bg-purple-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Pipeline
            </button>
            <button
              onClick={() => setView("leads")}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                view === "leads" ? "bg-purple-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Leads
            </button>
            <button
              onClick={() => setView("contacts")}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                view === "contacts" ? "bg-purple-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Clients
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-slate-800 border-slate-700 w-48"
            />
          </div>
          
          {/* Import Button */}
          {view === "pipeline" && (
            <Button
              onClick={() => setShowImportModal(true)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <Download className="w-4 h-4 mr-2" />
              Import
            </Button>
          )}
          
          {/* CSV Import Button */}
          <Button
            onClick={() => setShowCSVImportModal(true)}
            variant="outline"
            className="border-slate-700 text-indigo-400 hover:bg-indigo-500/10"
          >
            <Upload className="w-4 h-4 mr-2" />
            CSV Import
          </Button>
          
          {/* Add Button */}
          <Button
            onClick={() => view === "pipeline" ? setShowDealModal(true) : setShowContactModal(true)}
            className="bg-purple-500 hover:bg-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            {view === "pipeline" ? "New Deal" : "New Client"}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_contacts}</p>
                  <p className="text-xs text-slate-400">Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_deals}</p>
                  <p className="text-xs text-slate-400">Active Deals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_projects || 0}</p>
                  <p className="text-xs text-slate-400">Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.total_pipeline_value)}</p>
                  <p className="text-xs text-slate-400">Pipeline Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.won_value)}</p>
                  <p className="text-xs text-slate-400">Won Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pipeline View */}
      {view === "pipeline" && (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
          {stages.filter(s => s.id !== "lost").map((stage) => (
            <div
              key={stage.id}
              className={`flex-shrink-0 w-72 rounded-xl border ${STAGE_BG[stage.id] || STAGE_BG.lead}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
              data-testid={`pipeline-stage-${stage.id}`}
            >
              {/* Stage Header */}
              <div className="p-3 border-b border-slate-700/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-white">{stage.name}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {(dealsByStage[stage.id] || []).length}
                    </Badge>
                  </div>
                  <span className="text-xs text-slate-500">
                    {formatCurrency(stats?.stage_values?.[stage.id] || 0)}
                  </span>
                </div>
              </div>
              
              {/* Deals */}
              <div className="p-2 space-y-2 min-h-[200px]">
                {(dealsByStage[stage.id] || []).map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    onDragStart={handleDragStart}
                    onClick={() => {
                      setSelectedDeal(deal);
                      setShowDetailDrawer(true);
                    }}
                    formatCurrency={formatCurrency}
                  />
                ))}
                
                {/* Add Deal Button */}
                <button
                  onClick={() => {
                    setEditingDeal({ stage: stage.id });
                    setShowDealModal(true);
                  }}
                  className="w-full p-2 border border-dashed border-slate-600 rounded-lg text-slate-500 hover:text-slate-300 hover:border-slate-500 transition-colors text-sm flex items-center justify-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add deal
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Leads View */}
      {view === "leads" && (
        <div className="space-y-4" data-testid="leads-list">
          {/* Leads Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">
                {leads.length} lead{leads.length !== 1 ? 's' : ''} • Converts to client on contract signing
              </p>
            </div>
            <Button
              onClick={() => setShowLeadModal(true)}
              className="bg-purple-500 hover:bg-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </div>

          {leads.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center">
                <Target className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No leads yet</p>
                <p className="text-xs text-slate-500 mt-1">
                  Add prospects here. They automatically become clients when they sign a contract.
                </p>
                <Button
                  onClick={() => setShowLeadModal(true)}
                  className="mt-4 bg-purple-500 hover:bg-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add your first lead
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {leads
                .filter(lead => 
                  !searchTerm || 
                  lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  lead.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  lead.email?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((lead) => (
                <Card key={lead.id} className="bg-slate-800/50 border-slate-700 hover:border-purple-500/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="font-medium text-white">{lead.name}</h3>
                          {lead.company && (
                            <p className="text-sm text-slate-400 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {lead.company}
                            </p>
                          )}
                          {lead.email && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {lead.deal_count > 0 && (
                          <Badge variant="secondary" className="bg-blue-500/20 text-blue-400">
                            {lead.deal_count} deal{lead.deal_count !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="w-4 h-4 text-slate-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                            <DropdownMenuItem
                              onClick={() => handleConvertLead(lead.id)}
                              className="text-green-400"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Convert to Client
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingContact(lead);
                                setShowContactModal(true);
                              }}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit Lead
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingDeal({ contact_id: lead.id });
                                setShowDealModal(true);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Create Deal
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    {/* Show associated deals */}
                    {lead.deals && lead.deals.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-xs text-slate-500 mb-2">Associated Deals:</p>
                        <div className="flex flex-wrap gap-2">
                          {lead.deals.map(deal => (
                            <Badge 
                              key={deal.id} 
                              variant="outline" 
                              className="text-xs"
                            >
                              {deal.title} • {deal.stage}
                              {deal.value > 0 && ` • ${formatCurrency(deal.value)}`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Contacts View */}
      {view === "contacts" && (
        <div className="grid gap-4" data-testid="contacts-list">
          {filteredContacts.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No clients yet</p>
                <p className="text-xs text-slate-500 mt-1">Clients created here sync with your main Clients list</p>
                <Button
                  onClick={() => setShowContactModal(true)}
                  className="mt-4 bg-purple-500 hover:bg-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add your first client
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredContacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onClick={() => {
                  setSelectedContact(contact);
                  setShowDetailDrawer(true);
                }}
                onEdit={() => {
                  setEditingContact(contact);
                  setShowContactModal(true);
                }}
              />
            ))
          )}
        </div>
      )}

      {/* Deal Modal */}
      <DealModal
        open={showDealModal}
        onClose={() => {
          setShowDealModal(false);
          setEditingDeal(null);
        }}
        deal={editingDeal}
        contacts={contacts}
        stages={stages}
        onSave={async (data) => {
          try {
            if (editingDeal?.id) {
              await api.put(`/crm/deals/${editingDeal.id}`, data);
              toast.success("Deal updated");
            } else {
              // Add pipeline_id for new deals
              await api.post("/crm/deals", { ...data, pipeline_id: currentPipeline?.id });
              toast.success("Deal created");
            }
            fetchData(currentPipeline?.id);
            setShowDealModal(false);
            setEditingDeal(null);
          } catch (e) {
            toast.error("Failed to save deal");
          }
        }}
      />

      {/* Contact Modal */}
      <ContactModal
        open={showContactModal}
        onClose={() => {
          setShowContactModal(false);
          setEditingContact(null);
        }}
        contact={editingContact}
        onSave={async (data) => {
          try {
            if (editingContact?.id) {
              await api.put(`/crm/contacts/${editingContact.id}`, data);
              toast.success("Contact updated");
            } else {
              await api.post("/crm/contacts", data);
              toast.success("Contact created");
            }
            fetchData(currentPipeline?.id);
            setShowContactModal(false);
            setEditingContact(null);
          } catch (e) {
            toast.error("Failed to save contact");
          }
        }}
      />

      {/* Detail Drawer */}
      <DetailDrawer
        open={showDetailDrawer}
        onClose={() => {
          setShowDetailDrawer(false);
          setSelectedDeal(null);
          setSelectedContact(null);
        }}
        deal={selectedDeal}
        contact={selectedContact}
        onRefresh={() => fetchData(currentPipeline?.id)}
        formatCurrency={formatCurrency}
      />

      {/* Import Modal */}
      <ImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => {
          setShowImportModal(false);
          fetchData(currentPipeline?.id);
        }}
      />
      
      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={showCSVImportModal}
        onClose={() => setShowCSVImportModal(false)}
        onSuccess={() => {
          fetchData(currentPipeline?.id);
        }}
      />
      
      {/* Lead Modal */}
      <LeadModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSave={async (data) => {
          try {
            await api.post("/crm/contacts", { ...data, is_lead: true });
            toast.success("Lead created");
            fetchData(currentPipeline?.id);
            setShowLeadModal(false);
          } catch (e) {
            toast.error("Failed to create lead");
          }
        }}
      />
      
      {/* Pipeline Modal */}
      <PipelineModal
        open={showPipelineModal}
        onClose={() => {
          setShowPipelineModal(false);
          setEditingPipeline(null);
        }}
        pipeline={editingPipeline}
        onSave={editingPipeline ? handleUpdatePipeline : handleCreatePipeline}
        onDelete={editingPipeline && !editingPipeline.is_default ? () => handleDeletePipeline(editingPipeline.id) : null}
      />
    </div>
  );
}

// Deal Card Component
function DealCard({ deal, onDragStart, onClick, formatCurrency }) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      onClick={onClick}
      className="bg-slate-800 rounded-lg p-3 cursor-pointer hover:bg-slate-700/80 transition-colors border border-slate-700 group"
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-white text-sm line-clamp-1">{deal.title}</h4>
        <GripVertical className="w-4 h-4 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
      </div>
      
      {deal.contact && (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-400">
          <User className="w-3 h-3" />
          <span className="line-clamp-1">{deal.contact.name}</span>
        </div>
      )}
      
      <div className="flex items-center justify-between mt-2">
        {deal.value > 0 && (
          <span className="text-sm font-medium text-green-400">
            {formatCurrency(deal.value)}
          </span>
        )}
        {deal.expected_close && (
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(deal.expected_close).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

// Contact Card Component
function ContactCard({ contact, onClick, onEdit }) {
  return (
    <Card 
      className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
      onClick={onClick}
      data-testid={`contact-card-${contact.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
              <span className="text-purple-400 font-medium">
                {contact.name?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
            <div>
              <h3 className="font-medium text-white">{contact.name}</h3>
              {contact.company && (
                <p className="text-sm text-slate-400 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {contact.company}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {contact.email && (
              <a 
                href={`mailto:${contact.email}`}
                onClick={(e) => e.stopPropagation()}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4 text-slate-400" />
              </a>
            )}
            {contact.phone && (
              <a 
                href={`tel:${contact.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4 text-slate-400" />
              </a>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          {contact.email && <span>{contact.email}</span>}
          {contact.phone && <span>{contact.phone}</span>}
        </div>
        
        {contact.tags?.length > 0 && (
          <div className="flex gap-1 mt-2">
            {contact.tags.map((tag, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Deal Modal - Links deals to existing clients
function DealModal({ open, onClose, deal, contacts, stages, onSave }) {
  const [form, setForm] = useState({
    title: "",
    contact_id: "",
    stage: "lead",
    value: "",
    expected_close: "",
    description: ""
  });

  useEffect(() => {
    if (deal) {
      setForm({
        title: deal.title || "",
        contact_id: deal.contact_id || "",
        stage: deal.stage || "lead",
        value: deal.value || "",
        expected_close: deal.expected_close?.split("T")[0] || "",
        description: deal.description || ""
      });
    } else {
      setForm({
        title: "",
        contact_id: "",
        stage: "lead",
        value: "",
        expected_close: "",
        description: ""
      });
    }
  }, [deal, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">
            {deal?.id ? "Edit Deal" : "New Deal"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-300">Deal Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Website Redesign Project"
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          
          <div>
            <Label className="text-slate-300">Client</Label>
            <p className="text-xs text-slate-500 mb-1">Select from your existing clients</p>
            <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700">
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="none">No client</SelectItem>
                {contacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{c.company ? ` (${c.company})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Value ($)</Label>
              <Input
                type="number"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="5000"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-slate-300">Expected Close Date</Label>
            <Input
              type="date"
              value={form.expected_close}
              onChange={(e) => setForm({ ...form, expected_close: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          
          <div>
            <Label className="text-slate-300">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Add notes about this deal..."
              className="bg-slate-800 border-slate-700 mt-1"
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">
            Cancel
          </Button>
          <Button
            onClick={() => onSave({
              ...form,
              value: form.value ? parseFloat(form.value) : 0
            })}
            disabled={!form.title}
            className="bg-purple-500 hover:bg-purple-600"
          >
            {deal?.id ? "Update" : "Create"} Deal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Contact Modal - Creates clients in the unified clients collection
function ContactModal({ open, onClose, contact, onSave }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    title: "",
    website: "",
    linkedin: "",
    notes: ""
  });

  useEffect(() => {
    if (contact) {
      setForm({
        name: contact.name || "",
        email: contact.email || "",
        phone: contact.phone || "",
        company: contact.company || "",
        title: contact.title || "",
        website: contact.website || "",
        linkedin: contact.linkedin || "",
        notes: contact.notes || ""
      });
    } else {
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        title: "",
        website: "",
        linkedin: "",
        notes: ""
      });
    }
  }, [contact, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">
            {contact?.id ? "Edit Client" : "New Client"}
          </DialogTitle>
          <p className="text-sm text-slate-400">
            Clients created here are shared with your main Clients list
          </p>
        </DialogHeader>
        
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div>
            <Label className="text-slate-300">Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="John Smith"
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="john@example.com"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 555 123 4567"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Company</Label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Acme Inc"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Job Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Marketing Director"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-slate-300">Website</Label>
            <Input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://example.com"
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          
          <div>
            <Label className="text-slate-300">LinkedIn</Label>
            <Input
              value={form.linkedin}
              onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
              placeholder="https://linkedin.com/in/johnsmith"
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          
          <div>
            <Label className="text-slate-300">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Add notes about this contact..."
              className="bg-slate-800 border-slate-700 mt-1"
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">
            Cancel
          </Button>
          <Button
            onClick={() => onSave(form)}
            disabled={!form.name}
            className="bg-purple-500 hover:bg-purple-600"
          >
            {contact?.id ? "Update" : "Create"} Client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Detail Drawer (for viewing deal or contact details)
function DetailDrawer({ open, onClose, deal, contact, onRefresh, formatCurrency }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emails, setEmails] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newTask, setNewTask] = useState({ title: "", due_date: "", priority: "medium" });
  const [loading, setLoading] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);

  useEffect(() => {
    if (open && (deal || contact)) {
      fetchActivities();
      if (deal) {
        fetchTasks();
        fetchEmails();
      }
    }
  }, [open, deal, contact]);

  const fetchActivities = async () => {
    try {
      const params = new URLSearchParams();
      if (deal) params.set("deal_id", deal.id);
      if (contact) params.set("contact_id", contact.id);
      
      const res = await api.get(`/crm/activities?${params}`);
      setActivities(res.data.activities || []);
    } catch (e) {
      console.error("Failed to fetch activities", e);
    }
  };

  const fetchTasks = async () => {
    if (!deal) return;
    try {
      const res = await api.get(`/crm/deals/${deal.id}/tasks`);
      setTasks(res.data.tasks || []);
    } catch (e) {
      console.error("Failed to fetch tasks", e);
    }
  };

  const fetchEmails = async () => {
    if (!deal) return;
    try {
      const res = await api.get(`/crm/deals/${deal.id}/emails`);
      setEmails(res.data.emails || []);
    } catch (e) {
      console.error("Failed to fetch emails", e);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    setLoading(true);
    try {
      await api.post("/crm/activities", {
        type: "note",
        content: newNote,
        deal_id: deal?.id,
        contact_id: contact?.id
      });
      setNewNote("");
      fetchActivities();
      toast.success("Note added");
    } catch (e) {
      toast.error("Failed to add note");
    } finally {
      setLoading(false);
    }
  };

  const addTask = async () => {
    if (!newTask.title.trim() || !deal) return;
    
    setLoading(true);
    try {
      await api.post(`/crm/deals/${deal.id}/tasks`, newTask);
      setNewTask({ title: "", due_date: "", priority: "medium" });
      setShowTaskForm(false);
      fetchTasks();
      toast.success("Task added");
    } catch (e) {
      toast.error("Failed to add task");
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskStatus = async (task) => {
    try {
      const newStatus = task.status === "completed" ? "pending" : "completed";
      await api.put(`/crm/tasks/${task.id}`, { status: newStatus });
      fetchTasks();
      if (newStatus === "completed") {
        toast.success("Task completed! 🎉");
      }
    } catch (e) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/crm/tasks/${taskId}`);
      fetchTasks();
      toast.success("Task deleted");
    } catch (e) {
      toast.error("Failed to delete task");
    }
  };

  const entity = deal || contact;
  if (!entity) return null;

  const pendingTasks = tasks.filter(t => t.status !== "completed");
  const completedTasks = tasks.filter(t => t.status === "completed");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {deal ? (
              <>
                <Target className="w-5 h-5 text-purple-400" />
                {deal.title}
              </>
            ) : (
              <>
                <User className="w-5 h-5 text-blue-400" />
                {contact.name}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs - only show for deals */}
        {deal && (
          <div className="flex gap-1 bg-slate-800 rounded-lg p-1 -mt-2">
            {[
              { id: "overview", label: "Overview", icon: FileText },
              { id: "tasks", label: `Tasks${pendingTasks.length ? ` (${pendingTasks.length})` : ""}`, icon: ListTodo },
              { id: "emails", label: `Emails${emails.length ? ` (${emails.length})` : ""}`, icon: Mail },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded-md transition-colors ${
                  activeTab === tab.id
                    ? "bg-purple-500 text-white"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Overview Tab */}
          {(activeTab === "overview" || !deal) && (
            <>
              {/* Deal Info */}
              {deal && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge className={STAGE_COLORS[deal.stage] || "bg-slate-500/20 text-slate-300"}>
                      {deal.stage}
                    </Badge>
                    {deal.value > 0 && (
                      <span className="text-lg font-semibold text-green-400">
                        {formatCurrency(deal.value)}
                      </span>
                    )}
                  </div>
                  
                  {deal.contact && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <User className="w-4 h-4" />
                      <span>{deal.contact.name}</span>
                      {deal.contact.company && (
                        <span className="text-slate-600">• {deal.contact.company}</span>
                      )}
                    </div>
                  )}
                  
                  {deal.expected_close && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar className="w-4 h-4" />
                      <span>Expected: {new Date(deal.expected_close).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {deal.description && (
                    <p className="text-slate-400 text-sm">{deal.description}</p>
                  )}
                </div>
              )}
              
              {/* Contact Info */}
              {contact && (
                <div className="space-y-3">
                  {contact.company && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Building2 className="w-4 h-4" />
                      <span>{contact.company}</span>
                      {contact.title && <span className="text-slate-600">• {contact.title}</span>}
                    </div>
                  )}
                  
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-blue-400 hover:underline">
                      <Mail className="w-4 h-4" />
                      <span>{contact.email}</span>
                    </a>
                  )}
                  
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-slate-400 hover:text-white">
                      <Phone className="w-4 h-4" />
                      <span>{contact.phone}</span>
                    </a>
                  )}
                </div>
              )}
              
              {/* Add Note */}
              <div className="border-t border-slate-700 pt-4">
                <Label className="text-slate-300 text-sm">Add a note</Label>
                <div className="flex gap-2 mt-2">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Type your note..."
                    className="bg-slate-800 border-slate-700 text-sm"
                    rows={2}
                  />
                  <Button
                    onClick={addNote}
                    disabled={!newNote.trim() || loading}
                    className="bg-purple-500 hover:bg-purple-600 self-end"
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {/* Activity Timeline */}
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-slate-300 mb-3">Activity</h4>
                
                {activities.length === 0 ? (
                  <p className="text-slate-500 text-sm">No activity yet</p>
                ) : (
                  <div className="space-y-3">
                    {activities.slice(0, 10).map((activity) => (
                      <div key={activity.id} className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          activity.action === "email_synced" ? "bg-blue-500/20" :
                          activity.action === "task_completed" ? "bg-green-500/20" :
                          activity.type === "note" ? "bg-blue-500/20" :
                          activity.type === "system" ? "bg-slate-500/20" :
                          "bg-purple-500/20"
                        }`}>
                          {activity.action === "email_synced" ? <Mail className="w-4 h-4 text-blue-400" /> :
                           activity.action === "task_completed" ? <CheckCircle2 className="w-4 h-4 text-green-400" /> :
                           activity.type === "note" ? <FileText className="w-4 h-4 text-blue-400" /> :
                           activity.type === "system" ? <Clock className="w-4 h-4 text-slate-400" /> :
                           <MessageSquare className="w-4 h-4 text-purple-400" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-300">{activity.content}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(activity.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Tasks Tab */}
          {activeTab === "tasks" && deal && (
            <div className="space-y-4">
              {/* Add Task Button/Form */}
              {!showTaskForm ? (
                <Button
                  onClick={() => setShowTaskForm(true)}
                  variant="outline"
                  className="w-full border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-slate-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Task
                </Button>
              ) : (
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 space-y-3">
                  <Input
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="Task title..."
                    className="bg-slate-800 border-slate-700"
                  />
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={newTask.due_date?.split("T")[0] || ""}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value ? `${e.target.value}T12:00:00Z` : "" })}
                      className="bg-slate-800 border-slate-700 flex-1"
                    />
                    <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setShowTaskForm(false)} variant="outline" className="flex-1 border-slate-700">
                      Cancel
                    </Button>
                    <Button onClick={addTask} disabled={!newTask.title.trim() || loading} className="flex-1 bg-purple-500 hover:bg-purple-600">
                      Add Task
                    </Button>
                  </div>
                </div>
              )}

              {/* Pending Tasks */}
              {pendingTasks.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider">To Do</h4>
                  {pendingTasks.map(task => (
                    <div
                      key={task.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                        task.is_overdue
                          ? "bg-red-500/10 border-red-500/30"
                          : "bg-slate-800/50 border-slate-700"
                      }`}
                    >
                      <button
                        onClick={() => toggleTaskStatus(task)}
                        className="mt-0.5 text-slate-500 hover:text-green-400 transition-colors"
                      >
                        <Square className="w-5 h-5" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">{task.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {task.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${task.is_overdue ? "text-red-400" : "text-slate-500"}`}>
                              <Calendar className="w-3 h-3" />
                              {new Date(task.due_date).toLocaleDateString()}
                              {task.is_overdue && <AlertCircle className="w-3 h-3" />}
                            </span>
                          )}
                          <Badge variant="secondary" className={`text-[10px] ${
                            task.priority === "high" ? "bg-red-500/20 text-red-300" :
                            task.priority === "low" ? "bg-slate-500/20 text-slate-400" :
                            "bg-amber-500/20 text-amber-300"
                          }`}>
                            {task.priority}
                          </Badge>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="text-slate-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Completed Tasks */}
              {completedTasks.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider">Completed</h4>
                  {completedTasks.map(task => (
                    <div
                      key={task.id}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50"
                    >
                      <button
                        onClick={() => toggleTaskStatus(task)}
                        className="mt-0.5 text-green-400"
                      >
                        <CheckSquare className="w-5 h-5" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-500 line-through">{task.title}</p>
                      </div>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="text-slate-600 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {tasks.length === 0 && !showTaskForm && (
                <div className="text-center py-8">
                  <ListTodo className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No tasks yet</p>
                  <p className="text-xs text-slate-500 mt-1">Add tasks to track follow-ups and to-dos</p>
                </div>
              )}
            </div>
          )}

          {/* Emails Tab */}
          {activeTab === "emails" && deal && (
            <div className="space-y-4">
              {/* Sync Status */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-center justify-between">
                <div className="text-sm">
                  <p className="text-slate-300">{emails.length} emails synced</p>
                  <p className="text-xs text-slate-500">From contacts linked to this deal</p>
                </div>
                <Button
                  onClick={fetchEmails}
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-400"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Email List */}
              {emails.length > 0 ? (
                <div className="space-y-2">
                  {emails.map(email => (
                    <div
                      key={email.id}
                      className="bg-slate-800/50 border border-slate-700 rounded-lg p-3"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          email.direction === "inbound" ? "bg-blue-500/20" : "bg-green-500/20"
                        }`}>
                          {email.direction === "inbound" ? (
                            <Inbox className="w-4 h-4 text-blue-400" />
                          ) : (
                            <SendIcon className="w-4 h-4 text-green-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{email.subject}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {email.direction === "inbound" ? `From: ${email.from_name || email.from_email}` : `To: ${email.to_name || email.to_email}`}
                          </p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{email.snippet}</p>
                          <p className="text-xs text-slate-600 mt-2">
                            {new Date(email.email_date).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No emails synced yet</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Connect Gmail in Settings and sync to see emails here
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Import Modal - Import proposals and contracts as CRM deals
function ImportModal({ open, onClose, onImportComplete }) {
  const [tab, setTab] = useState("proposals"); // proposals | contracts
  const [proposals, setProposals] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedProposals, setSelectedProposals] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);

  useEffect(() => {
    if (open) {
      fetchImportables();
    }
  }, [open]);

  const fetchImportables = async () => {
    setLoading(true);
    try {
      const [proposalsRes, contractsRes] = await Promise.all([
        api.get("/crm/import/proposals"),
        api.get("/crm/import/contracts")
      ]);
      setProposals(proposalsRes.data.proposals || []);
      setContracts(contractsRes.data.contracts || []);
    } catch (e) {
      toast.error("Failed to load importable items");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (selectedProposals.length === 0 && selectedContracts.length === 0) {
      toast.error("Please select at least one item to import");
      return;
    }

    setImporting(true);
    try {
      const res = await api.post("/crm/import/bulk", {
        proposal_ids: selectedProposals,
        contract_project_ids: selectedContracts
      });
      
      const { proposals_imported, contracts_imported, skipped, errors } = res.data;
      
      if (proposals_imported > 0 || contracts_imported > 0) {
        toast.success(`Imported ${proposals_imported} proposals and ${contracts_imported} contracts`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} items were already imported`);
      }
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} errors occurred during import`);
      }
      
      onImportComplete();
    } catch (e) {
      toast.error("Failed to import items");
    } finally {
      setImporting(false);
    }
  };

  const toggleProposal = (id) => {
    setSelectedProposals(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleContract = (projectId) => {
    setSelectedContracts(prev => 
      prev.includes(projectId) ? prev.filter(p => p !== projectId) : [...prev, projectId]
    );
  };

  const selectAllProposals = () => {
    if (selectedProposals.length === proposals.length) {
      setSelectedProposals([]);
    } else {
      setSelectedProposals(proposals.map(p => p.id));
    }
  };

  const selectAllContracts = () => {
    if (selectedContracts.length === contracts.length) {
      setSelectedContracts([]);
    } else {
      setSelectedContracts(contracts.map(c => c.project_id));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-purple-400" />
            Import to CRM
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Import your existing proposals and contracts as CRM deals
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex bg-slate-800 rounded-lg p-1 mt-2">
          <button
            onClick={() => setTab("proposals")}
            className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-center gap-2 ${
              tab === "proposals" ? "bg-purple-500 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            <FileText className="w-4 h-4" />
            Proposals ({proposals.length})
          </button>
          <button
            onClick={() => setTab("contracts")}
            className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-center gap-2 ${
              tab === "contracts" ? "bg-purple-500 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            <FileSignature className="w-4 h-4" />
            Contracts ({contracts.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4 min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : (
            <>
              {/* Proposals Tab */}
              {tab === "proposals" && (
                <div className="space-y-2">
                  {proposals.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No proposals to import</p>
                      <p className="text-xs text-slate-500 mt-1">All proposals are already in your CRM</p>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={selectAllProposals}
                        className="text-sm text-purple-400 hover:text-purple-300 mb-2"
                      >
                        {selectedProposals.length === proposals.length ? "Deselect All" : "Select All"}
                      </button>
                      {proposals.map((p) => (
                        <div
                          key={p.id}
                          onClick={() => toggleProposal(p.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedProposals.includes(p.id)
                              ? "bg-purple-500/10 border-purple-500/50"
                              : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                            selectedProposals.includes(p.id)
                              ? "bg-purple-500 border-purple-500"
                              : "border-slate-600"
                          }`}>
                            {selectedProposals.includes(p.id) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{p.title}</p>
                            <p className="text-xs text-slate-500">
                              {p.client_name && <span>{p.client_name}</span>}
                              {p.client_company && <span className="text-slate-600"> • {p.client_company}</span>}
                            </p>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {p.status || "draft"}
                          </Badge>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Contracts Tab */}
              {tab === "contracts" && (
                <div className="space-y-2">
                  {contracts.length === 0 ? (
                    <div className="text-center py-8">
                      <FileSignature className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No contracts to import</p>
                      <p className="text-xs text-slate-500 mt-1">All contracts are already in your CRM</p>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={selectAllContracts}
                        className="text-sm text-purple-400 hover:text-purple-300 mb-2"
                      >
                        {selectedContracts.length === contracts.length ? "Deselect All" : "Select All"}
                      </button>
                      {contracts.map((c) => (
                        <div
                          key={c.project_id}
                          onClick={() => toggleContract(c.project_id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedContracts.includes(c.project_id)
                              ? "bg-purple-500/10 border-purple-500/50"
                              : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                            selectedContracts.includes(c.project_id)
                              ? "bg-purple-500 border-purple-500"
                              : "border-slate-600"
                          }`}>
                            {selectedContracts.includes(c.project_id) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">
                              {c.project_title || c.title || "Contract"}
                            </p>
                            <p className="text-xs text-slate-500">
                              {c.client_name && <span>{c.client_name}</span>}
                              {c.client_company && <span className="text-slate-600"> • {c.client_company}</span>}
                            </p>
                          </div>
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              c.signature_status === "signed" 
                                ? "bg-green-500/20 text-green-300" 
                                : c.signature_status === "pending"
                                ? "bg-amber-500/20 text-amber-300"
                                : ""
                            }`}
                          >
                            {c.signature_status || "unsigned"}
                          </Badge>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Info */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-xs text-slate-400">
          <p><strong className="text-slate-300">Proposals</strong> will be added to the <span className="text-purple-400">Proposal</span> stage</p>
          <p className="mt-1"><strong className="text-slate-300">Signed contracts</strong> will be added to <span className="text-green-400">Won</span>, unsigned to <span className="text-amber-400">Negotiation</span></p>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="border-slate-700">
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={importing || (selectedProposals.length === 0 && selectedContracts.length === 0)}
            className="bg-purple-500 hover:bg-purple-600"
          >
            {importing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Importing...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Import {selectedProposals.length + selectedContracts.length} Items
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Pipeline Modal - Create/Edit pipelines
function PipelineModal({ open, onClose, pipeline, onSave, onDelete }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (pipeline) {
      setForm({
        name: pipeline.name || "",
        description: pipeline.description || "",
      });
    } else {
      setForm({
        name: "",
        description: "",
      });
    }
  }, [pipeline, open]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Pipeline name is required");
      return;
    }
    setLoading(true);
    try {
      await onSave(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-400" />
            {pipeline ? "Edit Pipeline" : "New Pipeline"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {pipeline 
              ? "Update your pipeline details" 
              : "Create a new pipeline to organize your deals"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-300">Pipeline Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Services Pipeline"
              className="bg-slate-800 border-slate-700 mt-1"
              data-testid="pipeline-name-input"
            />
          </div>

          <div>
            <Label className="text-slate-300">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What is this pipeline for?"
              className="bg-slate-800 border-slate-700 mt-1"
              rows={3}
            />
          </div>

          {pipeline && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
              <p className="text-sm text-slate-400">
                <span className="font-medium text-white">{pipeline.deal_count || 0}</span> deals in this pipeline
              </p>
              {pipeline.is_default && (
                <p className="text-xs text-purple-400 mt-1">
                  This is your default pipeline
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div>
            {onDelete && (
              <Button
                variant="destructive"
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border-red-500/30"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="border-slate-700">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!form.name.trim() || loading}
              className="bg-purple-500 hover:bg-purple-600"
              data-testid="pipeline-save-btn"
            >
              {loading ? "Saving..." : (pipeline ? "Update" : "Create")} Pipeline
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// Lead Modal - Create a new lead
function LeadModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        notes: ""
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Lead name is required");
      return;
    }
    setLoading(true);
    try {
      await onSave(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-400" />
            Add Lead
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Add a prospect. They'll convert to a client when they sign a contract.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-300">Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Contact or company name"
              className="bg-slate-800 border-slate-700 mt-1"
              data-testid="lead-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 234 567 8900"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-slate-300">Company</Label>
            <Input
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="Company name"
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>

          <div>
            <Label className="text-slate-300">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any additional notes about this lead..."
              className="bg-slate-800 border-slate-700 mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.name.trim() || loading}
            className="bg-purple-500 hover:bg-purple-600"
            data-testid="lead-save-btn"
          >
            {loading ? "Saving..." : "Add Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

